import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
// Added API services for authenticated calls
import scanReportsService from '../services/scanReports.js';
import groupReviewsService from '../services/groupReviews.js';
import dashboardService from '../services/dashboard.js';

// Removed unused BACKEND variable

const EMPTY_REPORT = {
  generated_at: new Date().toISOString(),
  total_groups: 0,
  total_keywords: 0,
  top_groups: [],
  common_keywords: [],
  group_details: [],
};

const normalizeGroupIdentifier = (value) =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\/$/, '');

const getDiscoverGroupKey = (group) => {
  const normalizedLink = normalizeGroupIdentifier(group?.link || '');
  if (normalizedLink) return `link:${normalizedLink}`;
  const normalizedName = normalizeGroupIdentifier(group?.group_title || group?.name || '');
  return normalizedName ? `name:${normalizedName}` : '';
};

const buildDiscoverFingerprint = (groups, keywords, keywordQuery = '') => {
  const groupKeys = (Array.isArray(groups) ? groups : [])
    .map(getDiscoverGroupKey)
    .filter(Boolean)
    .sort();
  const keywordKeys = (Array.isArray(keywords) ? keywords : [])
    .map((k) => normalizeGroupIdentifier(k))
    .filter(Boolean)
    .sort();
  const normalizedQuery = normalizeGroupIdentifier(keywordQuery);
  return `${groupKeys.join('|')}::${keywordKeys.join('|')}::${normalizedQuery}`;
};

const hashKey = (value) => {
  const input = String(value || '');
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return `${(h2 >>> 0).toString(36)}${(h1 >>> 0).toString(36)}`;
};

const buildLocationLabel = (scan) =>
  String(
    scan?.scanLocationLabel ||
    scan?.locationTarget?.label ||
    [scan?.scanLocationCity || scan?.locationTarget?.city, scan?.scanLocationState || scan?.locationTarget?.state]
      .filter(Boolean)
      .join(', ')
  ).trim();

const buildSearchQueryPreview = (scan) => {
  const explicitQueries = Array.isArray(scan?.searchQueriesUsed) ? scan.searchQueriesUsed.filter(Boolean) : [];
  if (explicitQueries.length > 0) {
    return explicitQueries.slice(0, 3).join(' OR ');
  }

  const baseQuery = String(scan?.keywordQuery || '').trim();
  const locationLabel = buildLocationLabel(scan);
  if (!baseQuery) return 'No keyword query';
  if (!locationLabel) return baseQuery;

  const phrases = Array.isArray(scan?.keywords) && scan.keywords.length > 0
    ? scan.keywords
    : baseQuery.split(/\s+OR\s+/i).map((item) => item.trim()).filter(Boolean);
  return phrases.map((phrase) => `${phrase} ${locationLabel}`.trim()).join(' OR ');
};

const normalizeReviewStatus = (review) => {
  const status = String(review?.status || review?.reviewStatus || '').trim().toLowerCase();
  if (status === 'fake' || status === 'fraud' || status === 'fraudulent') return 'fraudulent';
  if (status === 'suspicious') return 'suspicious';
  if (status === 'genuine' || status === 'legit') return 'genuine';
  return 'unknown';
};

const getReviewUniqueKey = (review) => {
  const linkKey = normalizeGroupIdentifier(review?.groupLink || review?.group_link || '');
  if (linkKey) return `link:${linkKey}`;
  const nameKey = normalizeGroupIdentifier(review?.groupName || review?.group_name || '');
  if (nameKey) return `name:${nameKey}`;
  return review?.id ? `id:${review.id}` : '';
};

const getPeriodRange = (period) => {
  const now = new Date();
  const endDate = now.toISOString();
  const start = new Date(now);
  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === 'monthly') {
    start.setDate(start.getDate() - 30);
  } else {
    start.setDate(start.getDate() - 7);
  }
  return { startDate: start.toISOString(), endDate };
};


export default function Home() {
  const [report, setReport] = useState(EMPTY_REPORT);
  const [publicReports, setPublicReports] = useState([]);
  const [scanReports, setScanReports] = useState([]);
  const [groupReviews, setGroupReviews] = useState({});
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [period, setPeriod] = useState('7d');
  const [showAllScanReports, setShowAllScanReports] = useState(false);
  const [error, setError] = useState(null);
  const [syncInfo, setSyncInfo] = useState('');
  // Removed unused syncing state
  const [loading, setLoading] = useState(true);
  const autoSyncedReportKeyRef = useRef('');

  useEffect(() => {
    // Firestore publicReports subscription
    const q = query(collection(db, 'publicReports'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setPublicReports(snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
        };
      }));
    });

    return () => {
      unsub();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDashboardData = async () => {
      setLoading(true);
      try {
        const range = getPeriodRange(period);
        const [summaryResponse, reportsResponse, reviewsResponse] = await Promise.all([
          dashboardService.getDashboardData(period),
          scanReportsService.getReports({
            scope: 'global',
            limit: 25,
            orderBy: 'createdAt',
            order: 'desc',
            startDate: range.startDate,
            endDate: range.endDate,
          }),
          groupReviewsService.getReviews({
            limit: 200,
            startDate: range.startDate,
            endDate: range.endDate,
          }),
        ]);

        if (cancelled) return;

        const summaryData = summaryResponse?.data || null;
        setDashboardSummary(summaryData);

        const scans = reportsResponse?.reports || [];
        setScanReports(scans);

        const reviews = {};
        (reviewsResponse?.reviews || []).forEach((data) => {
          if (data.groupLink) {
            reviews[`link:${normalizeGroupIdentifier(data.groupLink)}`] = data;
          }
          if (data.groupName) {
            reviews[`name:${normalizeGroupIdentifier(data.groupName)}`] = data;
          }
        });
        setGroupReviews(reviews);

        const totalGroups = summaryData?.totalGroups ?? scans.reduce((sum, scan) => sum + (scan.groupsDiscovered || 0), 0);
        const totalKeywords = scans.reduce((sum, scan) => sum + (scan.keywords?.length || 0), 0);
        setReport({
          generated_at: new Date().toISOString(),
          total_groups: totalGroups,
          total_keywords: totalKeywords,
          top_groups: [],
          common_keywords: scans.flatMap(scan => scan.keywords || []).slice(0, 10),
          group_details: []
        });
        setError(null);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch dashboard data:', error);
        setReport(EMPTY_REPORT);
        setDashboardSummary(null);
        setScanReports([]);
        setGroupReviews({});
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDashboardData();
    const stopPolling = dashboardService.startPolling((data) => {
      if (cancelled || !data?.success) return;
      setDashboardSummary(data.data || null);
    }, period, 60000);

    return () => {
      cancelled = true;
      if (typeof stopPolling === 'function') stopPolling();
    };
  }, [period]);

  const filteredScanReports = useMemo(() => {
    const now = new Date();
    const byDate = scanReports.filter((scan) => {
      const fallbackMs = scan.createdAtMs || scan.updatedAtMs || null;
      const scanDate = scan.createdAt || scan.updatedAt || (fallbackMs ? new Date(fallbackMs) : null);
      if (!scanDate) return false;
      const created = new Date(scanDate);
      if (period === 'today') {
        return created.toDateString() === now.toDateString();
      }
      if (period === '7d') {
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        return created >= sevenDaysAgo;
      }
      const monthAgo = new Date(now);
      monthAgo.setMonth(now.getMonth() - 1);
      return created >= monthAgo;
    });
    return byDate.sort((a, b) => {
      const aDate = new Date(a.createdAt || a.updatedAt || a.createdAtMs || a.updatedAtMs || 0).getTime();
      const bDate = new Date(b.createdAt || b.updatedAt || b.createdAtMs || b.updatedAtMs || 0).getTime();
      return bDate - aDate;
    });
  }, [scanReports, period]);

  const dedupedFilteredScanReports = useMemo(() => {
    const seen = new Set();
    return filteredScanReports.filter((scan) => {
      const signature = scan.scanFingerprint
        ? `${scan.scanType || ''}|${scan.scanFingerprint}`
        : `${scan.scanType || ''}|${scan.operationId || ''}|${scan.keywordQuery || ''}|${scan.groupsDiscovered || 0}|${scan.totalHits || 0}`;
      if (seen.has(signature)) return false;
      seen.add(signature);
      return true;
    });
  }, [filteredScanReports]);

  const visibleScanReports = useMemo(() => {
    return dedupedFilteredScanReports.map((scan) => {
      const isReportWithGroups = (scan.scanType === 'discover' || scan.scanType === 'unified') && 
                                 Array.isArray(scan.scanGroupKeys) && 
                                 scan.scanGroupKeys.length > 0;
      if (!isReportWithGroups) return scan;

      const statuses = { ...(scan.reviewedGroupStatuses || {}) };
      scan.scanGroupKeys.forEach((key) => {
        const review = groupReviews[key];
        if (review?.status) statuses[key] = review.status;
      });

      let fraudulentCount = 0;
      let genuineCount = 0;
      scan.scanGroupKeys.forEach((key) => {
        if (statuses[key] === 'fake') fraudulentCount += 1;
        if (statuses[key] === 'genuine') genuineCount += 1;
      });

      const groupsDiscovered = scan.scanGroupKeys.length;
      const unmarkedCount = Math.max(groupsDiscovered - fraudulentCount - genuineCount, 0);
      const locationLabel = buildLocationLabel(scan);
      const queryPreview = buildSearchQueryPreview(scan);

      return {
        ...scan,
        fraudulentCount,
        genuineCount,
        unmarkedCount,
        officerFraudReviewedCount: fraudulentCount,
        officerGenuineReviewedCount: genuineCount,
        locationLabel,
        queryPreview,
      };
    });
  }, [dedupedFilteredScanReports, groupReviews]);

  const dashboardStats = useMemo(() => {
    const latestReviewsByGroup = new Map();
    Object.values(groupReviews).forEach((review) => {
      const key = getReviewUniqueKey(review);
      if (!key) return;
      const timestamp = Number(review?.reviewedAtMs || review?.timestampMs || review?.updatedAtMs || 0);
      const existing = latestReviewsByGroup.get(key);
      if (!existing || timestamp >= existing.timestamp) {
        latestReviewsByGroup.set(key, {
          timestamp,
          status: normalizeReviewStatus(review),
        });
      }
    });
    const manualFraudReviewed = Array.from(latestReviewsByGroup.values())
      .filter((review) => review.status === 'fraudulent')
      .length;

    const totalScans = visibleScanReports.length > 0
      ? visibleScanReports.length
      : Number(dashboardSummary?.totalScans ?? 0);
    const groupsDiscovered = visibleScanReports.length > 0
      ? visibleScanReports.reduce((sum, scan) => sum + Number(scan.groupsDiscovered || 0), 0)
      : Number(dashboardSummary?.totalGroups ?? 0);
    const fraudReviewed = manualFraudReviewed;
    const pendingReview = publicReports.filter((reportItem) => !reportItem.done).length;

    const stateFraudCounts = new Map();
    visibleScanReports.forEach((scan) => {
      const state = String(scan.scanLocationState || scan.locationTarget?.state || '').trim();
      if (!state) return;
      const fraudCount = Number(scan.officerFraudReviewedCount ?? 0) || 0;
      stateFraudCounts.set(state, (stateFraudCounts.get(state) || 0) + fraudCount);
    });

    let topStateScanned = '-';
    let topStateCount = 0;
    stateFraudCounts.forEach((count, state) => {
      if (count > topStateCount) {
        topStateScanned = state;
        topStateCount = count;
      }
    });

    return {
      totalScans,
      groupsDiscovered,
      fraudReviewed,
      topStateScanned: topStateCount > 0 ? topStateScanned : '-',
      pendingReview,
    };
  }, [visibleScanReports, publicReports, dashboardSummary, groupReviews]);

  const statCards = [
    { title: 'Total Scans', value: dashboardStats.totalScans.toString(), color: '#0f766e', icon: 'scan' },
    { title: 'Groups Discovered', value: dashboardStats.groupsDiscovered.toString(), color: '#174a2c', icon: 'users' },
    { title: 'Fraud Reviewed', value: dashboardStats.fraudReviewed.toString(), color: '#dc2626', icon: 'shield-alert' },
    { title: 'Top State by Fraud Cases', value: dashboardStats.topStateScanned, color: '#2563eb', icon: 'map-pin' },    { title: 'Pending Review', value: dashboardStats.pendingReview.toString(), color: '#f59e0b', icon: 'clock' },
  ];

  const quickActions = [
    {
      title: 'Telegram Search',
      subtitle: 'Run a new Telegram discovery scan.',
      route: '/telegram-search',
      color: '#0f766e',
    },
    {
      title: 'Reviewed DB',
      subtitle: 'Review, block, and manage flagged groups.',
      route: '/fraud',
      color: '#dc2626',
    },
    {
      title: 'Live Reports',
      subtitle: 'Check public reports submitted for review.',
      route: '/publicReport',
      color: '#2563eb',
    },
    {
      title: 'Guide',
      subtitle: 'Open setup steps and app feature help.',
      route: '/guide',
      color: '#f59e0b',
    },
  ];

  const scanReportsForDisplay = useMemo(() => {
    if (showAllScanReports) return visibleScanReports;
    const latestUnified = visibleScanReports.find((scan) => scan.scanType === 'unified');
    const latestDiscover = visibleScanReports.find((scan) => scan.scanType === 'discover');
    const latestPhrase = visibleScanReports.find((scan) => scan.scanType === 'phrase');

    const picked = [];
    if (latestUnified) picked.push(latestUnified);
    if (latestDiscover && latestDiscover.id !== latestUnified?.id) picked.push(latestDiscover);
    if (latestPhrase && !picked.some(p => p.id === latestPhrase.id)) picked.push(latestPhrase);

    if (picked.length < 2) {
      visibleScanReports.forEach((scan) => {
        if (picked.length >= 2) return;
        if (!picked.some((p) => p.id === scan.id)) picked.push(scan);
      });
    }

    return picked;
  }, [visibleScanReports, showAllScanReports]);

  useEffect(() => {
    setShowAllScanReports(false);
  }, [period]);

  const downloadScanReport = () => {
    if (!visibleScanReports.length) return;
    const headers = [
      'Date',
      'Scanned By',
      'Scan Type',
      'State',
      'City',
      'Location',
      'Keyword Query',
      'Queries Used',
      'Hits-Keywords',
      'Phrase Hits',
      'Groups',
      'Public Groups',
      'Private Groups',
      'Total Hits',
      'Fraudulent',
      'Genuine',
      'Unmarked'
    ];

    const rows = visibleScanReports.map((scan) => {
      const rowDate = scan.createdAt || scan.updatedAt || (scan.createdAtMs || scan.updatedAtMs ? new Date(scan.createdAtMs || scan.updatedAtMs) : null);
      const scanType = scan.scanType === 'unified' ? 'Telegram Search' : (scan.scanType || '');
      const keywords = Array.isArray(scan.matchedKeywordsFound) ? scan.matchedKeywordsFound.join(' | ') : '';
      
      return [
        rowDate ? new Date(rowDate).toLocaleString() : 'N/A',
        scan.scannerEmail || scan.officerId || '',
        scanType,
        scan.scanLocationState || scan.locationTarget?.state || '',
        scan.scanLocationCity || scan.locationTarget?.city || '',
        buildLocationLabel(scan),
        scan.keywordQuery || '',
        (Array.isArray(scan.searchQueriesUsed) ? scan.searchQueriesUsed : []).join(' | '),
        keywords,
        scan.totalPhraseHits || 0,
        scan.groupsDiscovered || 0,
        scan.publicCount || 0,
        scan.privateCount || 0,
        scan.totalHits || 0,
        scan.fraudulentCount || 0,
        scan.genuineCount || 0,
        scan.unmarkedCount || 0
      ].map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.setAttribute('download', `scan_report_${period}.csv`);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const buildScanReportPayload = useCallback((source) => {
    const groups = Array.isArray(report?.group_details) ? report.group_details : [];
    if (!groups.length) return null;

    const totalHits = groups.reduce((sum, g) => sum + (Number(g?.total_hits) || 0), 0);
    const storedKeywordQuery = (localStorage.getItem('last_discover_keyword_query') || '').trim();
    let storedKeywords = [];
    try {
      const raw = localStorage.getItem('last_discover_keywords');
      storedKeywords = raw ? JSON.parse(raw) : [];
    } catch {
      storedKeywords = [];
    }
    const keywords = Array.isArray(storedKeywords)
      ? storedKeywords.map((k) => String(k).trim()).filter(Boolean)
      : [];
    const keywordQuery = storedKeywordQuery || keywords.join(', ');
    const matchedKeywordsFound = [...new Set(
      groups.flatMap((g) => Object.keys(g?.matched_keywords || {}))
    )];

    const scanGroupKeys = groups.map(getDiscoverGroupKey).filter(Boolean);
    const scanFingerprint = buildDiscoverFingerprint(groups, keywords, keywordQuery);
    const reviewedGroupStatuses = {};
    let fraudulentCount = 0;
    let genuineCount = 0;

    scanGroupKeys.forEach((key) => {
      const review = groupReviews[key];
      if (!review?.status) return;
      reviewedGroupStatuses[key] = review.status;
      if (review.status === 'fake') fraudulentCount += 1;
      if (review.status === 'genuine') genuineCount += 1;
    });

    const unmarkedCount = Math.max(scanGroupKeys.length - fraudulentCount - genuineCount, 0);

    return {
      scanType: 'unified',
      keywords,
      keywordQuery,
      matchedKeywordsFound,
      groupsDiscovered: scanGroupKeys.length,
      totalHits,
      totalPhraseHits: groups.reduce((sum, g) => sum + (Number(g?.phrase_hits) || 0), 0),
      totalKeywordHits: totalHits,
      fraudulentCount,
      genuineCount,
      unmarkedCount,
      scanGroupKeys,
      reviewedGroupStatuses,
      operationId: '',
      createdAt: serverTimestamp(),
      source,
      reportGeneratedAt: report?.generated_at || '',
      scanFingerprint
    };
  }, [report, groupReviews]);

  const upsertHomeSyncedReport = useCallback(async (payload) => {
    if (!payload?.scanFingerprint) return false;

    const syncDocId = `home_${hashKey(payload.scanFingerprint)}`;
    const syncDocRef = doc(db, 'scan_reports', syncDocId);

    const existing = scanReports.find(
      (scan) =>
        (scan.source === 'home-sync-auto' || scan.source === 'home-sync-manual') &&
        (scan.scanFingerprint === payload.scanFingerprint || scan.id === syncDocId)
    );

    if (existing?.id) {
      await updateDoc(doc(db, 'scan_reports', existing.id), {
        ...payload,
        updatedAtMs: Date.now(),
        updatedAt: serverTimestamp(),
      });
      return false;
    }

    await setDoc(syncDocRef, {
      ...payload,
      createdAtMs: Date.now(),
      updatedAtMs: Date.now(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return true;
  }, [scanReports]);

  useEffect(() => {
    const reportKey = String(report?.generated_at || '');
    if (!reportKey) return;

    const payload = buildScanReportPayload('home-sync-auto');
    if (!payload) return;

    const alreadySynced = scanReports.some(
      (scan) =>
        scan.source === 'home-sync-auto' &&
        scan.scanFingerprint === payload.scanFingerprint &&
        scan.reportGeneratedAt === reportKey
    );
    if (alreadySynced) {
      autoSyncedReportKeyRef.current = reportKey;
      return;
    }

    if (autoSyncedReportKeyRef.current === reportKey) return;

    let cancelled = false;
    (async () => {
      try {
        // Removed setSyncing(true) - sync state not used in UI
        const created = await upsertHomeSyncedReport(payload);
        if (!cancelled) {
          autoSyncedReportKeyRef.current = reportKey;
          setSyncInfo(created ? 'Auto-synced latest backend report into Firebase.' : 'Updated existing synced report in Firebase.');
        }
      } catch (e) {
        if (!cancelled) {
          setSyncInfo(`Auto-sync failed: ${e.message}`);
        }
      } finally {
        // Removed setSyncing(false) - sync state not used in UI
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [report, scanReports, buildScanReportPayload, upsertHomeSyncedReport]);

  return (
    <div className="page-shell">
      <header className="top-bar">
        <div>
          <h1 className="page-title">Threat Detection Dashboard</h1>
          <p className="page-subtitle">Monitor and analyze cyber threats across platforms.</p>
        </div>
        </header>

      <div className="dash-grid">
        <section className="card stat-wrap">
          <div className="section-head">
            <div>
              <h3 className="section-title">Overview</h3>
              <p className="muted">{new Date(report.generated_at).toLocaleString()}</p>
            </div>
            {loading && <div className="pill">Loading</div>}
            {error && !loading && <div className="pill pill-warn">{error}</div>}
          </div>
          <div className="stat-grid">
            {statCards.map((s) => (
              <div className="stat-card" key={s.title}>
                <div className="stat-icon" style={{ background: s.color }}>{s.title[0]}</div>
                <div className="stat-body">
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.title}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="card quick-actions-card">
          <div className="section-head">
            <div>
              <div className="eyebrow">Navigation</div>
              <h3 className="section-title">Quick Actions</h3>
            </div>
          </div>
          <div className="quick-grid">
            {quickActions.map((q) => (
              <a className="quick-card" href={q.route} key={q.title}>
                <div className="quick-icon" style={{ background: q.color }}>{q.title[0]}</div>
                <div className="quick-body">
                  <div className="quick-title">{q.title}</div>
                  <div className="quick-sub">{q.subtitle}</div>
                </div>
                <div className="quick-arrow">›</div>
              </a>
            ))}
          </div>
        </section>

        <section className="card home-report-card">
          <div className="section-head home-report-head">
            <div>
              <div className="eyebrow">Officer Activity</div>
              <h3 className="section-title">Scan Performance Report</h3>
              <p className="muted">Track each scan done in Telegram Search.</p>
            </div>
            <div className="pill">{visibleScanReports.length} scans</div>
          </div>

          <div className="home-report-controls">
            <select className="form-input home-report-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="today">Today</option>
              <option value="7d">Last 7 Days</option>
              <option value="1m">Last 1 Month</option>
            </select>
            <button className="primary-btn home-report-download" onClick={downloadScanReport} disabled={visibleScanReports.length === 0}>
              Download Report CSV
            </button>
          </div>

          
          {syncInfo && <p className="muted">{syncInfo}</p>}

          {visibleScanReports.length === 0 ? (
            <p className="muted">No scan data found for selected period.</p>
          ) : (
            <div className="list home-report-list">
              {scanReportsForDisplay.map((scan) => (
                <div className="list-item home-report-item" key={scan.id}>
                  <div>
                    <div className="list-title">
                      {scan.scanType === 'unified' ? 'Telegram Search' : (scan.scanType === 'discover' ? 'Discover Scan' : 'Phrase Scan')}
                    </div>
                    <div className="list-sub">
                      <div>{scan.queryPreview || buildSearchQueryPreview(scan)}</div>
                      {buildLocationLabel(scan) && (
                        <div>State scan: {buildLocationLabel(scan)}</div>
                      )}
                      {(scan.scannerEmail || scan.officerId) && (
                        <div>Scanned by: {scan.scannerEmail || scan.officerId}</div>
                      )}
                    </div>
                  </div>
                  <div className="home-report-stats">
                    <span className="tag">Groups: {scan.groupsDiscovered || 0}</span>
                    {(scan.publicCount > 0 || scan.privateCount > 0) && (
                      <>
                        <span className="tag" style={{ border: '1px solid #e2e8f0', background: '#fff' }}>Public: {scan.publicCount || 0}</span>
                        <span className="tag" style={{ border: '1px solid #e2e8f0', background: '#fff' }}>Private: {scan.privateCount || 0}</span>
                      </>
                    )}
                    <span className="tag">Hits: {scan.totalHits || 0}</span>
                    {scan.totalPhraseHits > 0 && <span className="tag" style={{ background: '#dcfce7', color: '#166534' }}>Phrase Hits: {scan.totalPhraseHits}</span>}
                    <span className="tag tag-high">Fraud reviewed: {scan.officerFraudReviewedCount ?? scan.fraudulentCount ?? 0}</span>
                    <span className="tag tag-success">Genuine reviewed: {scan.officerGenuineReviewedCount ?? scan.genuineCount ?? 0}</span>
                    <span className="tag">Unmarked: {scan.unmarkedCount || 0}</span>
                  </div>
                </div>
              ))}
              {visibleScanReports.length > 2 && (
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowAllScanReports((prev) => !prev)}
                >
                  {showAllScanReports ? 'Read less' : `Read more (${visibleScanReports.length - 2} more)`}
                </button>
              )}
            </div>
          )}
        </section>

        {/* <section className="card">
          <div className="section-head">
            <div>
              <div className="eyebrow">Public Reports</div>
              <h3 className="section-title">Recent submissions</h3>
              <p className="muted">This month: {reportsThisMonth.length} · This week: {reportsThisWeek.length}</p>
            </div>
          </div>
          {recentReports.length === 0 ? (
            <p className="muted">No public reports yet.</p>
          ) : (
            <ul className="list">
              {recentReports.map((r) => (
                <li className="list-item" key={r.id}>
                  <div>
                    <div className="list-title">{r.title || 'Public Report'}</div>
                    <div className="list-sub">{r.summary || 'No summary provided.'}</div>
                  </div>
                  <div className="list-meta">{r.createdAt ? new Date(r.createdAt).toLocaleString() : 'N/A'}</div>
                </li>
              ))}
            </ul>
          )}
        </section> */}
      </div>
    </div>
  );
}

